import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { CurrentShiftStage } from "@src/appV2/redesign/Shift/constants";
import { getShiftCurrentStage } from "@src/appV2/redesign/Shift/WorkerShift/getShiftCurrentStage";
import { isRebrandingEnabled } from "@src/appV2/redesign/system/ui-theme";
import { type Shift } from "@src/appV2/Shifts/Shift/types";
import { differenceInMinutes, parseISO } from "date-fns";

import { getIsShiftEndingSoon } from "../utils/getIsShiftEndingSoon";
import { getScheduledEndDateFromShift } from "../utils/getScheduledEndDate";
import { BreakActions } from "./BreakActions";
import { ClockOutAction } from "./ClockOutAction";
import { ExtendShiftAction } from "./ExtendShiftAction";

const SHOW_CLOCK_OUT_THRESHOLD_IN_MINUTES = 60;

interface TimekeepingActionsProps {
  shift: Shift;
  onStartBreak: () => void;
  onEndBreak: () => void;
  onClockOut: () => void;
  onSkipBreak: () => void;
  showBreakActivity: boolean;
  isShiftExtendable?: boolean;
  hasShiftExtension?: boolean;
  isShiftEnded?: boolean;
  onExtendShift?: () => void;
  isCapAtShiftLength?: boolean;
}

export function TimekeepingActions(props: TimekeepingActionsProps) {
  const {
    shift,
    onStartBreak,
    onEndBreak,
    onClockOut,
    onSkipBreak,
    showBreakActivity,
    isShiftExtendable = false,
    hasShiftExtension = false,
    isShiftEnded = false,
    onExtendShift,
    isCapAtShiftLength = false,
  } = props;

  const currentDate = useCurrentDate(1000);
  const shiftStartDate = parseISO(shift.start);
  const shiftEndDate = parseISO(shift.end);
  const clockInDate = isDefined(shift.clockInOut?.start)
    ? parseISO(shift.clockInOut.start)
    : undefined;

  const currentStage = getShiftCurrentStage(shift);
  const isOnBreak = currentStage === CurrentShiftStage.BREAK_STARTED;
  const hasEndedBreak = currentStage === CurrentShiftStage.BREAK_ENDED;
  const hasSkippedBreak = currentStage === CurrentShiftStage.BREAK_SKIPPED;
  const scheduledEndDate = getScheduledEndDateFromShift(shift, isCapAtShiftLength);
  const shouldShowClockOut =
    !showBreakActivity ||
    hasEndedBreak ||
    hasSkippedBreak ||
    differenceInMinutes(scheduledEndDate, currentDate) < SHOW_CLOCK_OUT_THRESHOLD_IN_MINUTES;

  const isShiftEndingSoon = getIsShiftEndingSoon({
    shiftStartDate,
    shiftEndDate,
    clockInDate,
    isCapAtShiftLength,
    now: currentDate,
  });

  return (
    <Stack
      sx={{
        position: "fixed",
        bottom: isRebrandingEnabled() ? 94 : 70,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1,
        gap: 1,
        alignItems: "center",
      }}
    >
      {!shouldShowClockOut && showBreakActivity && (
        <BreakActions
          isOnBreak={isOnBreak}
          hasEndedBreak={hasEndedBreak}
          hasSkippedBreak={hasSkippedBreak}
          onStartBreak={onStartBreak}
          onEndBreak={onEndBreak}
          onSkipBreak={onSkipBreak}
        />
      )}

      {shouldShowClockOut && (
        <Stack direction="row" gap={2} sx={{ whiteSpace: "nowrap", "& > *": { flex: 1 } }}>
          {isShiftExtendable &&
            isShiftEndingSoon &&
            !hasShiftExtension &&
            !isShiftEnded &&
            isDefined(onExtendShift) && <ExtendShiftAction onExtendShift={onExtendShift} />}
          <ClockOutAction onClockOut={onClockOut} />
        </Stack>
      )}
    </Stack>
  );
}
